import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { PACKAGE_INTERESTS } from '../data/packages';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { useCart } from '../utils/cart';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PublicPartnerProducts } from '../components/PublicPartnerProducts';
import { MarketplaceCard } from '../components/MarketplaceCard';

const DEST_GROUPS = ['Todos', 'São Tomé', 'Príncipe', 'São Tomé + Príncipe', 'Santiago'];

// Tour categories — a category chip only renders when at least one real published product matches.
const TOUR_CATEGORIES = [
  { id: 'day', match: (p) => parseInt(p.duration) <= 1 },
  { id: 'private', match: (p) => /privad/i.test(p.style || '') },
  { id: 'island', match: (p) => ['Ilha Completa', 'Ilha Signature', 'Explorador da Ilha', 'Duas Ilhas'].includes(p.style) },
  { id: 'multiday', match: (p) => parseInt(p.duration) >= 2 },
  { id: 'cultural', match: (p) => (PACKAGE_INTERESTS[p.slug] || []).includes('Cultura') },
  { id: 'nature', match: (p) => (PACKAGE_INTERESTS[p.slug] || []).some((i) => ['Natureza', 'Aventura', 'Caminhada'].includes(i)) },
  { id: 'stp', match: (p) => p.destination.includes('São Tomé') || p.destination.includes('Príncipe') },
  { id: 'cv', match: (p) => p.destination.includes('Cabo Verde') || p.destination.includes('Santiago') },
];

const destMatch = (p, g) =>
  g === 'Santiago' ? p.destination.includes('Santiago') : p.destination === g;

export default function ToursPage() {
  const { t } = useLang();
  const { packages } = useCatalog();
  const { add: addToCart, includes: cartIncludes } = useCart();
  const [params] = useSearchParams();
  useSEO(`${t('tours.t')} | Eco Collection`, t('tours.s'));
  const [dest, setDest] = useState(() => {
    const d = params.get('destination') || '';
    return DEST_GROUPS.includes(d) ? d : (d === 'Príncipe' ? 'Príncipe' : 'Todos');
  });
  const [category, setCategory] = useState('all');

  const activeCategories = TOUR_CATEGORIES.filter((c) => packages.some(c.match));

  const visible = packages.filter((p) => {
    if (dest !== 'Todos' && !destMatch(p, dest)) return false;
    if (category !== 'all' && !TOUR_CATEGORIES.find((c) => c.id === category)?.match(p)) return false;
    return true;
  });

  const addToTrip = (p) => {
    const result = addToCart({ type: 'package', slug: p.slug, name: p.name, destination: p.destination });
    if (!result.added) toast(t('cart.duplicate'));
    else toast.success(t('tours.added'));
  };

  const selectCls = 'rounded-full border border-eco-ink/15 bg-white px-4 py-2 text-sm text-eco-ink/70 outline-none focus:border-eco-gold';

  return (
    <div data-testid="tours-page">
      <PageHeader kicker={t('tours.k')} title={t('tours.t')} sub={t('tours.s')} />
      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p data-testid="tours-intro" className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-eco-ink/60 md:text-base">{t('tours.intro')}</p>
          </Reveal>

          <Reveal className="mt-8 flex flex-wrap items-center justify-center gap-2" data-testid="tours-categories">
            <button data-testid="tours-cat-all" onClick={() => setCategory('all')} className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${category === 'all' ? 'bg-eco-green text-eco-gold' : 'bg-white text-eco-ink/60 hover:text-eco-ink'}`}>{t('tours.cat.all')}</button>
            {activeCategories.map((c) => (
              <button key={c.id} data-testid={`tours-cat-${c.id}`} onClick={() => setCategory(c.id)} className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${category === c.id ? 'bg-eco-green text-eco-gold' : 'bg-white text-eco-ink/60 hover:text-eco-ink'}`}>
                {t(`tours.cat.${c.id}`)}
              </button>
            ))}
            <select data-testid="tours-filter-destination" value={dest} onChange={(e) => setDest(e.target.value)} className={selectCls}>
              {DEST_GROUPS.map((g) => <option key={g} value={g}>{g === 'Todos' ? `${t('filter.destination')}: ${t('filter.all')}` : g === 'Santiago' ? 'Santiago, Cabo Verde' : g}</option>)}
            </select>
            <span data-testid="tours-count" className="text-sm text-eco-ink/50">{visible.length}</span>
          </Reveal>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="tours-grid">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <div className="flex h-full flex-col">
                  <MarketplaceCard
                    to={`/journeys/${p.slug}`}
                    testid={`tour-card-${p.slug}`}
                    image={p.img}
                    imageBadge={p.destination}
                    eyebrow={`${p.destination} · ${p.style}`}
                    title={p.name}
                    location={p.idealFor}
                    duration={p.duration}
                    partner={t('partners.kicker')}
                    cta={t('tours.explore')}
                  />
                  <button
                    type="button"
                    data-testid={`tour-add-${p.slug}`}
                    onClick={() => addToTrip(p)}
                    className="eco-btn-text mt-1 self-start text-xs"
                  >
                    <Heart size={13} className={cartIncludes('package', p.slug) ? 'fill-eco-gold text-eco-gold' : ''} /> {t('tours.add')}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          {visible.length === 0 && (
            <div data-testid="tours-empty" className="mt-10 rounded-3xl border border-dashed border-eco-ink/15 bg-white py-14 text-center">
              <p className="font-serif text-xl text-eco-ink/70">{t('empty.all')}</p>
              <Link to="/plan-my-trip" data-testid="tours-empty-cta" className="group mt-6 inline-flex items-center gap-2 rounded-full bg-eco-green px-7 py-3 text-sm font-semibold text-white hover:bg-eco-deep">
                {t('nav.plan')} <ArrowRight size={15} />
              </Link>
            </div>
          )}
          <PublicPartnerProducts type="package" island={dest} />
        </div>
      </section>
    </div>
  );
}
